// Workshop topics data with full content for each topic
export const workshopTopicsData = {
  "job-search-cover-letters": {
    title: "Job Search & Cover Letters",
    icon: "FileText",
    summary: "A strategic job search combined with a compelling cover letter can be the difference between landing your dream job and getting lost in a sea of applicants. Understanding how to effectively search for opportunities and craft personalized cover letters is essential for career success in today's competitive job market.",
    importance: "In today's digital age, employers receive hundreds of applications for each position. A well-crafted cover letter serves as your personal introduction, highlighting why you're the perfect fit before they even review your resume. Combined with a strategic job search approach, you can significantly increase your chances of getting noticed.",
    keyPoints: [
      {
        title: "Research Before You Apply",
        description: "Take time to research each company thoroughly. Understand their mission, values, recent news, and company culture. This knowledge will help you tailor your application and demonstrate genuine interest."
      },
      {
        title: "Customize Every Cover Letter",
        description: "Never use a generic cover letter. Address it to a specific person when possible, reference the exact position, and explain why you're interested in that particular company and role."
      },
      {
        title: "Lead with Impact",
        description: "Open your cover letter with a strong hook that captures attention. Mention a specific achievement, connection to the company, or compelling reason why you're the ideal candidate."
      },
      {
        title: "Quantify Your Achievements",
        description: "Use specific numbers and metrics to demonstrate your impact. Instead of 'increased sales,' say 'increased sales by 35% within six months through strategic client outreach.'"
      },
      {
        title: "Address the Job Requirements",
        description: "Map your skills and experiences directly to the job requirements. Show the employer you've read the job description and explain how you meet their specific needs."
      },
      {
        title: "Maintain Professional Formatting",
        description: "Keep your cover letter to one page, use a professional font, and ensure consistent formatting. Proofread multiple times for spelling and grammar errors."
      },
      {
        title: "Include a Strong Call to Action",
        description: "End with a clear call to action expressing your enthusiasm for an interview. Thank them for their consideration and indicate your availability for follow-up."
      }
    ],
    callToAction: "Ready to master the art of job searching and cover letter writing? Request our comprehensive workshop for your organization."
  },

  "building-linkedin-profile": {
    title: "Building a LinkedIn Profile",
    icon: "Linkedin",
    summary: "LinkedIn has become the world's largest professional networking platform, with over 900 million members worldwide. A well-optimized LinkedIn profile is essential for personal branding, networking, job searching, and establishing yourself as a thought leader in your industry.",
    importance: "Recruiters and hiring managers actively use LinkedIn to find candidates. Studies show that 87% of recruiters use LinkedIn to vet candidates during the hiring process. Your profile serves as your digital first impression and can open doors to opportunities you never knew existed.",
    keyPoints: [
      {
        title: "Professional Profile Photo",
        description: "Use a high-quality, professional headshot where your face takes up about 60% of the frame. Dress appropriately for your industry and use good lighting with a clean background."
      },
      {
        title: "Compelling Headline",
        description: "Your headline is prime real estate. Go beyond just your job title-include keywords, your value proposition, and what makes you unique. Example: 'Marketing Director | Helping B2B Tech Companies Increase Revenue Through Data-Driven Strategies'"
      },
      {
        title: "Engaging About Section",
        description: "Write a compelling summary in first person that tells your professional story. Include your passions, accomplishments, and what you're looking for. Use keywords relevant to your industry."
      },
      {
        title: "Detailed Experience Section",
        description: "Don't just list job titles-describe your responsibilities and achievements with specific metrics. Use action verbs and quantify your impact wherever possible."
      },
      {
        title: "Skills and Endorsements",
        description: "Add relevant skills to your profile and arrange them strategically. The top three skills are most visible, so prioritize the ones most important to your career goals."
      },
      {
        title: "Recommendations",
        description: "Request recommendations from colleagues, managers, and clients who can speak to your work quality. Aim for diverse perspectives that highlight different aspects of your professional abilities."
      },
      {
        title: "Engage Consistently",
        description: "Post content regularly, engage with others' posts, join relevant groups, and share industry insights. Active profiles get significantly more visibility in searches."
      },
      {
        title: "Customize Your URL",
        description: "Create a custom LinkedIn URL with your name for a more professional appearance and easier sharing on resumes and business cards."
      }
    ],
    callToAction: "Transform your LinkedIn presence from ordinary to outstanding. Book our LinkedIn optimization workshop today."
  },

  "business-etiquette": {
    title: "Business Etiquette",
    icon: "Briefcase",
    summary: "Business etiquette encompasses the unwritten rules that govern professional interactions. Mastering these social norms can significantly impact your career success, helping you build stronger relationships, make positive impressions, and navigate the professional world with confidence.",
    importance: "In business, people make judgments within seconds of meeting you. Proper etiquette demonstrates respect, professionalism, and cultural awareness. It can be the deciding factor in job interviews, client meetings, and professional advancement opportunities.",
    keyPoints: [
      {
        title: "The Art of Introduction",
        description: "When introducing yourself or others, stand up, make eye contact, offer a firm handshake, and clearly state names. In formal settings, introduce the lower-ranking person to the higher-ranking person."
      },
      {
        title: "Professional Communication",
        description: "Respond to emails and calls promptly. Use appropriate salutations and sign-offs. Proofread all written communication. Be mindful of tone in digital communications where context can be lost."
      },
      {
        title: "Meeting Etiquette",
        description: "Arrive on time or early. Come prepared with necessary materials. Silence your phone. Listen actively and avoid interrupting. Follow up with action items and thank-you notes."
      },
      {
        title: "Dining Etiquette",
        description: "Know basic table manners for business meals. Place your napkin on your lap, use utensils from the outside in, and wait for everyone to be served before eating. The host typically signals when to begin."
      },
      {
        title: "Digital Etiquette",
        description: "Be professional on video calls-check your background, lighting, and audio. Keep your camera on when appropriate. Mute yourself when not speaking. Don't multitask visibly during meetings."
      },
      {
        title: "Dress Appropriately",
        description: "Understand your company's dress code and dress slightly above it for important occasions. When in doubt, err on the side of being more formal rather than too casual."
      },
      {
        title: "Respect Personal Space and Time",
        description: "Be mindful of others' time and personal boundaries. Don't overstay your welcome in meetings or conversations. Respect cultural differences in personal space preferences."
      }
    ],
    callToAction: "Elevate your professional presence with our comprehensive business etiquette workshop."
  },

  "career-fair-strategies": {
    title: "Career Fair Strategies",
    icon: "Users",
    summary: "Career fairs provide unique opportunities to meet multiple employers in one setting, learn about various industries, and make personal connections that can lead to internships and job offers. Success at career fairs requires strategic preparation before, during, and after the event.",
    importance: "Unlike online applications, career fairs allow you to make a personal impression on recruiters. Many employers prioritize candidates they meet in person, and some positions are only advertised at these events. A well-executed career fair strategy can fast-track your job search.",
    keyPoints: [
      {
        title: "Research Attending Companies",
        description: "Before the fair, review the list of attending employers. Research companies that interest you, understand their open positions, and prepare specific questions that show genuine interest."
      },
      {
        title: "Prepare Your Elevator Pitch",
        description: "Craft a 30-60 second introduction that highlights your background, skills, and career goals. Practice until it sounds natural, not rehearsed. Customize it for different industries."
      },
      {
        title: "Dress Professionally",
        description: "Wear professional business attire appropriate for your target industry. Ensure clothes are clean, pressed, and fit well. Choose comfortable shoes as you'll be standing and walking."
      },
      {
        title: "Bring Essential Materials",
        description: "Carry multiple copies of your resume in a professional folder or portfolio. Bring a pen, notepad, and business cards if you have them. Keep your hands free for handshakes."
      },
      {
        title: "Strategic Booth Navigation",
        description: "Visit your most desired employers during less busy times. Start with a few 'warm-up' companies to practice your pitch. Make note of specific conversations for follow-up."
      },
      {
        title: "Ask Meaningful Questions",
        description: "Go beyond basic questions that can be answered on the company website. Ask about company culture, growth opportunities, day-to-day responsibilities, and what makes successful employees stand out."
      },
      {
        title: "Collect Contact Information",
        description: "Request business cards or LinkedIn connections from every recruiter you speak with. Write notes on the back about your conversation immediately after leaving each booth."
      },
      {
        title: "Follow Up Within 24-48 Hours",
        description: "Send personalized thank-you emails to each recruiter you spoke with. Reference specific points from your conversation and reiterate your interest in opportunities."
      }
    ],
    callToAction: "Maximize your career fair success with our strategic preparation workshop."
  },

  "career-portfolio": {
    title: "Career Portfolio",
    icon: "FolderOpen",
    summary: "A career portfolio is a curated collection of materials that showcase your skills, accomplishments, and professional growth. Whether digital or physical, a well-organized portfolio provides tangible evidence of your capabilities and sets you apart from other candidates.",
    importance: "While resumes tell employers what you've done, portfolios show them how well you did it. In interviews, a portfolio provides concrete examples to discuss and demonstrates your organizational skills and attention to detail.",
    keyPoints: [
      {
        title: "Choose Your Format",
        description: "Decide between a physical portfolio (binder with printed materials) or digital portfolio (website, PDF, or online platform). Consider your industry-creative fields typically expect digital portfolios with visual work."
      },
      {
        title: "Include Essential Documents",
        description: "Start with your resume, cover letter samples, transcripts, certifications, and licenses. Add letters of recommendation and professional references list."
      },
      {
        title: "Showcase Work Samples",
        description: "Include your best work that demonstrates relevant skills. This could be writing samples, project reports, presentations, designs, or any deliverables you've created professionally or academically."
      },
      {
        title: "Document Achievements",
        description: "Include awards, honors, publications, media mentions, and any recognition you've received. Add performance reviews or testimonials from supervisors and colleagues."
      },
      {
        title: "Demonstrate Skill Development",
        description: "Show evidence of professional development through training certificates, workshop completions, conference attendance, and continuing education courses."
      },
      {
        title: "Organize Strategically",
        description: "Arrange materials in a logical order with clear sections and labels. Use tabs or a table of contents for easy navigation. Lead with your strongest, most relevant pieces."
      },
      {
        title: "Keep It Updated",
        description: "Regularly add new accomplishments and remove outdated materials. Your portfolio should always reflect your current capabilities and career direction."
      },
      {
        title: "Practice Presenting",
        description: "Be prepared to walk through your portfolio in an interview. Know which pieces to highlight for different opportunities and have stories ready about each item."
      }
    ],
    callToAction: "Learn how to build a compelling career portfolio that opens doors. Request our workshop today."
  },

  "dress-for-success": {
    title: "Dress for Success",
    icon: "Shirt",
    summary: "The way you dress communicates volumes before you speak a single word. Professional attire demonstrates respect for the workplace, builds credibility, and influences how others perceive your competence. Understanding dress codes and making appropriate choices is a crucial professional skill.",
    importance: "Research shows that people form first impressions within seven seconds, and appearance plays a significant role. Dressing appropriately can increase your confidence, improve how others receive you, and impact your career trajectory.",
    keyPoints: [
      {
        title: "Understand Dress Code Categories",
        description: "Learn the differences between business formal (suits), business professional (blazers with dress pants/skirts), business casual (no jeans, polished look), and casual (clean, neat, appropriate)."
      },
      {
        title: "Research Company Culture",
        description: "Before interviews or first days, research the company's dress culture. Check their website, social media, or ask HR. When in doubt, dress one level above what you expect employees to wear."
      },
      {
        title: "Invest in Quality Basics",
        description: "Build a professional wardrobe with quality foundational pieces: well-fitted suits, classic blazers, quality dress shirts, appropriate shoes, and a professional bag or briefcase."
      },
      {
        title: "Ensure Proper Fit",
        description: "Clothes should fit well-not too tight or too loose. Consider tailoring for key pieces. Proper fit looks more polished than expensive clothes that don't fit well."
      },
      {
        title: "Pay Attention to Details",
        description: "Iron or steam clothes before wearing. Polish shoes regularly. Ensure hems are intact and buttons secure. Replace worn items promptly."
      },
      {
        title: "Grooming Matters",
        description: "Professional dress extends to grooming. Maintain neat hair, clean nails, fresh breath, and appropriate personal hygiene. Keep makeup and fragrances subtle in professional settings."
      },
      {
        title: "Dress for the Job You Want",
        description: "Consider how leaders in your desired position dress. Emulate professional style while maintaining your authentic self. Your clothes should support your career aspirations."
      },
      {
        title: "Prepare for Virtual Settings",
        description: "For video calls, dress professionally at least from the waist up. Solid colors work better on camera than busy patterns. Test your appearance on camera before important meetings."
      }
    ],
    callToAction: "Make the right first impression every time. Book our Dress for Success workshop."
  },

  "graduate-school": {
    title: "Graduate School: To Go or Not To Go",
    icon: "GraduationCap",
    summary: "Pursuing a graduate or professional degree is a significant investment of time, money, and energy. This important decision requires careful consideration of your career goals, financial situation, industry requirements, and personal circumstances to determine if an advanced degree will truly benefit your career.",
    importance: "While graduate education can open doors and increase earning potential in many fields, it's not the right choice for everyone. Understanding when graduate school makes sense-and when it doesn't-can save you years of time and thousands of dollars while optimizing your career path.",
    keyPoints: [
      {
        title: "Assess Your Career Goals",
        description: "Determine if your desired career path requires or strongly benefits from an advanced degree. Research job postings in your target field to see what credentials employers actually require."
      },
      {
        title: "Research Your Industry",
        description: "Some industries (academia, law, medicine) require advanced degrees. Others value experience over credentials. Talk to professionals in your desired field about the role education played in their careers."
      },
      {
        title: "Calculate the True Cost",
        description: "Consider tuition, fees, books, and living expenses. Factor in the opportunity cost of lost income during your studies. Calculate potential salary increases to determine ROI."
      },
      {
        title: "Explore Funding Options",
        description: "Research scholarships, fellowships, assistantships, employer tuition reimbursement, and loan options. Many programs offer funding that can significantly reduce out-of-pocket costs."
      },
      {
        title: "Consider Timing",
        description: "Evaluate whether to go immediately after undergraduate studies or gain work experience first. Some programs prefer or require professional experience; others prefer recent graduates."
      },
      {
        title: "Evaluate Program Options",
        description: "Compare full-time, part-time, online, and hybrid programs. Consider rankings, faculty expertise, alumni networks, and career services. Visit campuses and talk to current students."
      },
      {
        title: "Prepare Your Application",
        description: "Strong applications typically require standardized test scores, transcripts, recommendations, personal statements, and sometimes interviews. Start preparation early for competitive programs."
      },
      {
        title: "Have a Backup Plan",
        description: "Consider what you'll do if you don't get into your preferred programs or if circumstances change. Having alternatives reduces pressure and expands opportunities."
      }
    ],
    callToAction: "Make an informed decision about your educational future. Request our graduate school planning workshop."
  },

  "internship-search-strategies": {
    title: "Internship Search Strategies",
    icon: "Search",
    summary: "Internships provide invaluable real-world experience, professional connections, and often serve as a pathway to full-time employment. A strategic approach to finding, landing, and maximizing internship opportunities can launch your career in the right direction.",
    importance: "Over 70% of internships lead to full-time job offers, and employers increasingly view internship experience as essential for entry-level positions. The skills, connections, and insights gained through internships can shape your entire career trajectory.",
    keyPoints: [
      {
        title: "Start Early",
        description: "Begin your internship search 3-6 months before your desired start date. Many competitive programs have early deadlines, and the best opportunities fill quickly."
      },
      {
        title: "Utilize Multiple Resources",
        description: "Search through your school's career center, job boards (LinkedIn, Indeed, Handshake), company websites, professional associations, and networking contacts. Don't rely on just one source."
      },
      {
        title: "Target Strategically",
        description: "Identify companies and industries aligned with your career goals. Research which organizations offer strong internship programs with mentorship, projects, and potential for conversion."
      },
      {
        title: "Tailor Your Application",
        description: "Customize your resume and cover letter for each application. Highlight relevant coursework, projects, skills, and experiences. Show enthusiasm for the specific company and role."
      },
      {
        title: "Leverage Your Network",
        description: "Talk to professors, alumni, family friends, and anyone who might have connections. Many internships are filled through referrals and networking before they're publicly posted."
      },
      {
        title: "Prepare for Interviews",
        description: "Research common internship interview questions and practice your responses. Prepare specific examples of teamwork, problem-solving, and relevant skills. Have thoughtful questions ready."
      },
      {
        title: "Consider Various Opportunities",
        description: "Look beyond traditional corporate internships. Nonprofits, startups, government agencies, and research positions offer valuable experience and may have less competition."
      },
      {
        title: "Maximize the Experience",
        description: "Once you land an internship, treat it as an extended interview. Be proactive, ask questions, seek feedback, build relationships, and document your accomplishments."
      }
    ],
    callToAction: "Launch your career with the right internship. Book our internship search strategies workshop."
  },

  "interviewing": {
    title: "Interviewing Skills",
    icon: "UserCheck",
    summary: "The job interview is your opportunity to bring your resume to life and demonstrate why you're the best candidate for the position. Mastering interview skills-from preparation to follow-up-can significantly increase your chances of receiving job offers.",
    importance: "Even highly qualified candidates can struggle in interviews without proper preparation. Interview performance often determines hiring decisions, making it essential to develop strong skills in presenting yourself, answering questions, and building rapport with interviewers.",
    keyPoints: [
      {
        title: "Research Thoroughly",
        description: "Know the company's history, mission, products/services, recent news, and competitors. Understand the role requirements and how they fit into the organization. Research your interviewers on LinkedIn."
      },
      {
        title: "Prepare Your Stories",
        description: "Use the STAR method (Situation, Task, Action, Result) to structure responses. Prepare 5-7 stories that demonstrate key competencies and can be adapted for various behavioral questions."
      },
      {
        title: "Practice Common Questions",
        description: "Rehearse answers to common questions like 'Tell me about yourself,' 'Why this company?', 'Greatest strengths/weaknesses,' and 'Where do you see yourself in 5 years?'"
      },
      {
        title: "Master Non-Verbal Communication",
        description: "Maintain good eye contact, offer a firm handshake, sit up straight, and use open body language. Your non-verbal cues should convey confidence and enthusiasm."
      },
      {
        title: "Manage Interview Anxiety",
        description: "Arrive early to collect yourself. Take deep breaths. Remember that some nervousness is normal and can even enhance performance. Focus on having a conversation, not performing."
      },
      {
        title: "Ask Thoughtful Questions",
        description: "Prepare insightful questions about the role, team, culture, and growth opportunities. Asking good questions demonstrates interest and helps you evaluate if the job is right for you."
      },
      {
        title: "Handle Different Formats",
        description: "Be prepared for phone screens, video interviews, panel interviews, case studies, and skills assessments. Each format requires slightly different preparation and approach."
      },
      {
        title: "Follow Up Professionally",
        description: "Send thank-you emails within 24 hours to each interviewer. Reference specific conversation points and reiterate your interest. This small step can make a big difference."
      }
    ],
    callToAction: "Ace your next interview with confidence. Request our comprehensive interviewing workshop."
  },

  "job-search-strategies": {
    title: "Job Search Strategies",
    icon: "Target",
    summary: "An effective job search requires more than just applying to posted positions. Strategic job seekers use multiple methods, stay organized, and maintain persistence to uncover opportunities-including those in the hidden job market-and ultimately land their ideal role.",
    importance: "Studies show that up to 80% of jobs are filled through networking and are never publicly posted. Understanding how to tap into the hidden job market and employ diverse search strategies dramatically increases your chances of finding the right opportunity.",
    keyPoints: [
      {
        title: "Define Your Target",
        description: "Clearly identify your ideal role, industry, company size, culture, and location preferences. Having specific criteria helps focus your search and ensures you pursue opportunities that truly fit."
      },
      {
        title: "Optimize Your Materials",
        description: "Ensure your resume, cover letter, and online profiles are polished and tailored. Different versions may be needed for different types of roles or industries."
      },
      {
        title: "Utilize Multiple Channels",
        description: "Don't rely solely on job boards. Use company career pages, LinkedIn, professional associations, recruiters, career fairs, and alumni networks to find opportunities."
      },
      {
        title: "Network Strategically",
        description: "Reach out to contacts for informational interviews, attend industry events, and engage on LinkedIn. Many opportunities come through personal connections and referrals."
      },
      {
        title: "Stay Organized",
        description: "Track all applications, contacts, and follow-ups in a spreadsheet or job search app. Note deadlines, conversation details, and next steps for each opportunity."
      },
      {
        title: "Apply Strategically",
        description: "Quality matters more than quantity. Tailor each application to the specific role. Apply early when positions are posted-many companies review on a rolling basis."
      },
      {
        title: "Maintain Momentum",
        description: "Job searching is a numbers game. Set daily/weekly goals for applications and networking activities. Stay consistent even when facing rejections or slow responses."
      },
      {
        title: "Take Care of Yourself",
        description: "Job searching can be stressful. Maintain routines, exercise, and social connections. Treat the search like a job-with dedicated hours and breaks for self-care."
      }
    ],
    callToAction: "Develop a winning job search strategy. Book our comprehensive workshop today."
  },

  "job-shadowing": {
    title: "Job Shadowing",
    icon: "Eye",
    summary: "Job shadowing provides a unique opportunity to observe professionals in their daily work environment, gaining realistic insights into potential career paths. This experiential learning method helps you make informed career decisions based on firsthand observation rather than assumptions.",
    importance: "Career decisions based solely on job descriptions or media portrayals can lead to disappointment. Job shadowing reveals the reality of day-to-day work, helping you confirm interest in a career path or discover it's not the right fit before investing in education or job changes.",
    keyPoints: [
      {
        title: "Identify Your Goals",
        description: "Determine what you want to learn from the experience. Are you exploring a specific career, industry, or company? Having clear objectives helps you ask the right questions and focus your observations."
      },
      {
        title: "Find Opportunities",
        description: "Reach out to your network, use LinkedIn, contact professional associations, or ask your career center for help connecting with professionals willing to host shadows."
      },
      {
        title: "Request Professionally",
        description: "Send a polished request explaining who you are, why you're interested in their work, and what you hope to learn. Be flexible with scheduling and respectful of their time."
      },
      {
        title: "Prepare Questions",
        description: "Develop thoughtful questions about their career path, daily responsibilities, challenges, rewards, and advice. This shows professionalism and helps you maximize the experience."
      },
      {
        title: "Observe Actively",
        description: "Pay attention to the work environment, interactions, and tasks performed. Note things you find appealing and aspects that concern you. Be present and engaged throughout."
      },
      {
        title: "Professional Conduct",
        description: "Dress appropriately, arrive on time, be courteous to everyone, and follow workplace rules. Remember you're a guest and your behavior reflects on you."
      },
      {
        title: "Follow Up Appropriately",
        description: "Send a thank-you note within 24 hours expressing gratitude for their time. Mention specific things you learned or found valuable."
      },
      {
        title: "Reflect on the Experience",
        description: "After shadowing, reflect on what you observed. Does this career align with your interests and values? What additional information do you need? How has this shaped your career direction?"
      }
    ],
    callToAction: "Learn how to arrange and maximize job shadowing experiences. Request our workshop."
  },

  "negotiating-job-offers": {
    title: "Negotiating Job Offers",
    icon: "DollarSign",
    summary: "Salary and benefits negotiation is a critical skill that can impact your earnings significantly throughout your career. Learning to negotiate confidently and professionally can result in better compensation packages without jeopardizing job offers.",
    importance: "Studies show that failing to negotiate a starting salary can cost hundreds of thousands of dollars over a career. Yet many people, especially early-career professionals and women, don't negotiate because they don't know how or fear negative consequences.",
    keyPoints: [
      {
        title: "Know Your Worth",
        description: "Research salary ranges for your role, industry, location, and experience level using sites like Glassdoor, LinkedIn Salary, and Payscale. Talk to others in similar positions for real-world data."
      },
      {
        title: "Consider the Full Package",
        description: "Evaluate total compensation including base salary, bonuses, stock options, health benefits, retirement contributions, PTO, remote work options, professional development, and other perks."
      },
      {
        title: "Wait for the Right Time",
        description: "Don't discuss salary until you have an offer. Let the employer mention numbers first when possible. If asked early, deflect by focusing on fit and learning more about the role."
      },
      {
        title: "Express Enthusiasm First",
        description: "Before negotiating, express genuine excitement about the offer and the opportunity. This sets a positive tone and reassures the employer of your interest."
      },
      {
        title: "Make a Reasonable Counter",
        description: "Base your counteroffer on research, not arbitrary numbers. Aim slightly higher than your target to leave room for negotiation. Be prepared to justify your request."
      },
      {
        title: "Practice Your Script",
        description: "Rehearse what you'll say. Use confident language like 'Based on my research and experience, I was expecting something in the range of...' rather than apologetic phrases."
      },
      {
        title: "Handle Multiple Offers",
        description: "If you have competing offers, use them as leverage ethically. Be honest with employers about your timeline and that you're considering other opportunities."
      },
      {
        title: "Get Everything in Writing",
        description: "Once you reach agreement, request a written offer letter that includes all negotiated terms. Review carefully before accepting."
      }
    ],
    callToAction: "Negotiate with confidence and maximize your compensation. Book our workshop today."
  },

  "personal-branding": {
    title: "Personal Branding",
    icon: "Award",
    summary: "Your personal brand is how you present yourself professionally to the world-it's the unique combination of skills, experiences, and personality that you want others to associate with you. A strong personal brand differentiates you in a competitive job market and opens doors to opportunities.",
    importance: "In the digital age, your personal brand exists whether you actively manage it or not. People will Google you, check your LinkedIn, and form opinions. Taking control of your narrative ensures you're seen how you want to be seen and positions you as a thought leader in your field.",
    keyPoints: [
      {
        title: "Define Your Unique Value",
        description: "Identify what makes you distinctive. What combination of skills, experiences, and perspectives do you bring? What do you want to be known for? This is the foundation of your brand."
      },
      {
        title: "Identify Your Target Audience",
        description: "Determine who you want to reach-potential employers, clients, collaborators, or industry peers. Understanding your audience helps you craft relevant messages and choose appropriate platforms."
      },
      {
        title: "Craft Your Brand Statement",
        description: "Develop a clear, concise statement that captures who you are, what you do, and the value you provide. This becomes the basis for your elevator pitch and online profiles."
      },
      {
        title: "Align Your Online Presence",
        description: "Ensure consistency across LinkedIn, personal website, social media, and any online presence. Your photo, messaging, and tone should present a cohesive professional image."
      },
      {
        title: "Create and Share Content",
        description: "Establish expertise by sharing insights, writing articles, commenting on industry news, or speaking at events. Consistent content creation builds visibility and credibility."
      },
      {
        title: "Network Authentically",
        description: "Build relationships aligned with your brand. Engage meaningfully with others' content, offer help generously, and cultivate connections that support your professional goals."
      },
      {
        title: "Seek Testimonials and Endorsements",
        description: "Ask colleagues, clients, and supervisors to provide recommendations and endorsements that reinforce your brand. Third-party validation builds credibility."
      },
      {
        title: "Monitor and Evolve",
        description: "Regularly Google yourself to see what comes up. Update your brand as your career evolves. Authenticity is key-your brand should reflect who you genuinely are."
      }
    ],
    callToAction: "Build a personal brand that opens doors. Request our personal branding workshop."
  },

  "professional-communication": {
    title: "Professional Communication",
    icon: "MessageSquare",
    summary: "Effective communication is consistently ranked as one of the most important professional skills. Whether speaking, writing, or presenting, the ability to convey ideas clearly and appropriately determines your success in collaborating with colleagues, persuading stakeholders, and advancing your career.",
    importance: "Poor communication leads to misunderstandings, missed deadlines, damaged relationships, and lost opportunities. Strong communicators are more likely to be promoted, trusted with important projects, and seen as leadership material.",
    keyPoints: [
      {
        title: "Know Your Audience",
        description: "Adapt your message based on who you're communicating with. Consider their knowledge level, priorities, and communication preferences. What matters to a technical team differs from what matters to executives."
      },
      {
        title: "Be Clear and Concise",
        description: "Get to the point quickly. Use simple language and avoid jargon when possible. Structure your message logically with the most important information first."
      },
      {
        title: "Listen Actively",
        description: "Good communication is two-way. Pay full attention when others speak, ask clarifying questions, and paraphrase to confirm understanding. This builds trust and prevents misunderstandings."
      },
      {
        title: "Master Email Etiquette",
        description: "Write clear subject lines, keep messages brief, use proper formatting, and proofread before sending. Be mindful of tone-emails can easily be misinterpreted."
      },
      {
        title: "Present with Confidence",
        description: "When speaking publicly, prepare thoroughly, structure your presentation clearly, maintain eye contact, and engage your audience. Practice managing nervousness and handling questions."
      },
      {
        title: "Navigate Difficult Conversations",
        description: "Address conflicts professionally. Stay calm, focus on issues not personalities, seek to understand others' perspectives, and work toward solutions."
      },
      {
        title: "Adapt to Different Mediums",
        description: "Different channels require different approaches. In-person, phone, video, email, and chat all have distinct norms and best practices."
      },
      {
        title: "Seek and Provide Feedback",
        description: "Ask for feedback on your communication. Observe effective communicators and learn from them. Continuously improve through practice and reflection."
      }
    ],
    callToAction: "Elevate your professional communication skills. Book our comprehensive workshop."
  },

  "resume-critiquing": {
    title: "Resume Critiquing",
    icon: "FileEdit",
    summary: "A resume critique provides objective feedback on your resume's effectiveness, identifying areas for improvement in content, format, and impact. Regular review and refinement of your resume ensures it remains a powerful marketing tool that accurately represents your qualifications.",
    importance: "Most resumes receive only 6-7 seconds of initial review. A well-critiqued resume can make those seconds count, passing through applicant tracking systems and capturing recruiter attention to secure interviews.",
    keyPoints: [
      {
        title: "Start with Formatting",
        description: "Evaluate visual appeal and readability. Is there sufficient white space? Is the font professional and consistent? Can key information be found quickly? Does it appear organized and clean?"
      },
      {
        title: "Check ATS Compatibility",
        description: "Ensure your resume can be parsed by Applicant Tracking Systems. Use standard headings, avoid tables and graphics, and include relevant keywords from job descriptions."
      },
      {
        title: "Evaluate Content Relevance",
        description: "Does every item support your application? Remove outdated or irrelevant information. Tailor content to highlight experiences most relevant to your target roles."
      },
      {
        title: "Strengthen Achievement Statements",
        description: "Transform job duties into accomplishments. Use action verbs, quantify results when possible, and demonstrate the impact of your work rather than just listing responsibilities."
      },
      {
        title: "Review for Consistency",
        description: "Check that formatting, tense, punctuation, and style are consistent throughout. Inconsistencies suggest carelessness and distract from your content."
      },
      {
        title: "Eliminate Errors",
        description: "Proofread multiple times for spelling, grammar, and punctuation errors. Have others review it too-fresh eyes catch mistakes you might miss."
      },
      {
        title: "Assess Overall Impact",
        description: "Does the resume clearly communicate your value proposition? Would a recruiter understand your qualifications and be intrigued to learn more?"
      },
      {
        title: "Get Multiple Perspectives",
        description: "Seek feedback from career professionals, industry contacts, and peers. Different viewpoints help identify blind spots and improvement opportunities."
      }
    ],
    callToAction: "Get expert feedback on your resume. Join our resume critiquing workshop."
  },

  "resume-writing": {
    title: "Resume Writing & Cover Letters",
    icon: "PenTool",
    summary: "Your resume is often your first opportunity to make an impression on potential employers. A well-crafted resume clearly communicates your qualifications, achievements, and potential value to an organization, while a targeted cover letter personalizes your application.",
    importance: "Recruiters spend an average of just 6 seconds on initial resume review. Every word must count. A strong resume gets you interviews; a weak one gets you passed over regardless of your actual qualifications.",
    keyPoints: [
      {
        title: "Choose the Right Format",
        description: "Select chronological (most common), functional (skills-based), or combination format based on your experience and career situation. Most employers prefer chronological resumes."
      },
      {
        title: "Craft a Strong Summary",
        description: "Open with a compelling professional summary or objective that captures your key qualifications and career goals. This is your elevator pitch in written form."
      },
      {
        title: "Showcase Achievements",
        description: "Focus on accomplishments rather than just duties. Use the CAR formula (Challenge, Action, Result) to structure impactful bullet points. Quantify whenever possible."
      },
      {
        title: "Include Relevant Keywords",
        description: "Mirror language from job descriptions to pass ATS screening. Include industry-specific terms, required skills, and relevant certifications naturally throughout your resume."
      },
      {
        title: "Organize Information Strategically",
        description: "Place the most important and relevant information first. Use clear section headings. Limit to 1-2 pages depending on experience level and industry norms."
      },
      {
        title: "Format for Readability",
        description: "Use clean, professional fonts. Include adequate white space. Use consistent formatting for bullets, dates, and job titles. Make key information easy to scan."
      },
      {
        title: "Write Targeted Cover Letters",
        description: "Each cover letter should be customized for the specific role and company. Address specific requirements, show company knowledge, and explain why you're the ideal candidate."
      },
      {
        title: "Proofread Meticulously",
        description: "Errors can disqualify you immediately. Read aloud, use spell check, and have others review. Print it out-errors are often easier to catch on paper."
      }
    ],
    callToAction: "Create a resume that gets results. Request our resume writing workshop."
  },

  "social-media-job-search": {
    title: "Social Media and the Job Search",
    icon: "Share2",
    summary: "Social media has transformed how employers find candidates and how job seekers uncover opportunities. Understanding how to leverage social platforms professionally-and avoid common pitfalls-is essential for modern job search success.",
    importance: "Over 90% of recruiters use social media to find and vet candidates. Your social presence can either support your professional brand or undermine it. Learning to use these platforms strategically gives you a significant advantage in your job search.",
    keyPoints: [
      {
        title: "Audit Your Online Presence",
        description: "Google yourself. Review all social media profiles from an employer's perspective. Remove or privatize content that could reflect poorly on your professional image."
      },
      {
        title: "Optimize LinkedIn",
        description: "LinkedIn is the primary professional platform. Ensure your profile is complete, keyword-rich, and showcases your expertise. Engage actively with industry content."
      },
      {
        title: "Use Twitter Professionally",
        description: "Follow industry leaders, companies, and recruiters. Share relevant content and engage in professional discussions. Many jobs are shared on Twitter before job boards."
      },
      {
        title: "Leverage Facebook Groups",
        description: "Join professional groups and alumni networks related to your field. These often share job opportunities and provide valuable networking connections."
      },
      {
        title: "Research Companies",
        description: "Follow companies you're interested in on all platforms. This provides insights into culture, news, and sometimes job postings shared socially first."
      },
      {
        title: "Maintain Privacy Settings",
        description: "Keep personal social accounts private if you want to separate personal and professional lives. Be aware that nothing online is truly private."
      },
      {
        title: "Create Valuable Content",
        description: "Share industry insights, comment thoughtfully on others' posts, and demonstrate your expertise. This builds visibility and establishes you as a knowledgeable professional."
      },
      {
        title: "Network Authentically",
        description: "Connect with purpose, not just to increase numbers. Engage meaningfully with connections, offer help, and build genuine professional relationships."
      }
    ],
    callToAction: "Master social media for your job search. Book our workshop today."
  },

  "writing-personal-statement": {
    title: "Writing a Personal Statement",
    icon: "Edit3",
    summary: "Personal statements are required for graduate school applications, scholarships, fellowships, and many professional opportunities. A compelling personal statement tells your unique story, demonstrates your qualifications, and explains why you're the ideal candidate for the opportunity.",
    importance: "When qualifications are similar, the personal statement often determines who gets accepted or receives funding. It's your opportunity to go beyond grades and test scores to show who you are and why you matter.",
    keyPoints: [
      {
        title: "Understand the Prompt",
        description: "Read the prompt carefully and address all parts. Understand what the selection committee is looking for. Research the organization's values and priorities."
      },
      {
        title: "Tell Your Story",
        description: "Use narrative techniques to engage readers. Open with a compelling hook. Share specific experiences that shaped your journey and illustrate your points with concrete examples."
      },
      {
        title: "Show, Don't Just Tell",
        description: "Instead of stating you're 'passionate about healthcare,' describe a specific experience that ignited and demonstrates that passion. Let readers draw conclusions from your evidence."
      },
      {
        title: "Highlight Your Unique Perspective",
        description: "What makes your background, experiences, or approach distinctive? How will you contribute to the program or organization? Differentiate yourself from other applicants."
      },
      {
        title: "Connect Past to Future",
        description: "Explain how your experiences have prepared you for this opportunity and how this opportunity fits into your larger goals. Show clear motivation and direction."
      },
      {
        title: "Maintain Focus and Structure",
        description: "Every paragraph should serve a purpose. Create a clear through-line from beginning to end. Avoid tangents and unnecessary information."
      },
      {
        title: "Be Authentic",
        description: "Write in your genuine voice. Don't try to guess what readers want to hear. Authentic statements are more compelling and memorable than generic ones."
      },
      {
        title: "Edit Ruthlessly",
        description: "First drafts are never final. Revise multiple times. Cut unnecessary words. Have others provide feedback. Read aloud to catch awkward phrasing."
      }
    ],
    callToAction: "Craft a personal statement that stands out. Request our workshop today."
  },

  "career-development-overview": {
    title: "Career Development Overview",
    icon: "TrendingUp",
    summary: "A comprehensive introduction to career development strategies and professional growth. This foundational workshop provides participants with essential career development concepts, goal-setting frameworks, and actionable strategies for professional advancement.",
    importance: "Career development is not just about climbing the corporate ladder—it's about understanding your strengths, aligning your work with your values, and continuously growing as a professional. Organizations that invest in their employees' career development see higher retention rates, increased productivity, and a more engaged workforce.",
    keyPoints: [
      {
        title: "Self-Assessment and Awareness",
        description: "Understanding your skills, interests, values, and personality is the foundation of career development. Learn tools and frameworks for honest self-evaluation that will guide your career decisions."
      },
      {
        title: "Goal Setting Frameworks",
        description: "Learn how to set SMART career goals (Specific, Measurable, Achievable, Relevant, Time-bound) that provide clear direction and motivation for your professional journey."
      },
      {
        title: "Career Pathing Strategies",
        description: "Explore different career paths within your industry and organization. Understand lateral moves, vertical advancement, and how to position yourself for opportunities."
      },
      {
        title: "Skill Gap Analysis",
        description: "Identify the skills you need for your desired role and create actionable plans to develop them through training, mentorship, and practical experience."
      },
      {
        title: "Building Your Professional Brand",
        description: "Learn the basics of personal branding—how to present yourself consistently and authentically across all professional touchpoints."
      },
      {
        title: "Networking Fundamentals",
        description: "Discover the importance of professional relationships and learn strategies for building and maintaining a valuable network."
      },
      {
        title: "Creating Your Development Plan",
        description: "Walk away with a personalized career development action plan that includes short-term and long-term goals, resources needed, and accountability measures."
      }
    ],
    callToAction: "Ready to invest in your team's professional growth? Request our Career Development Overview workshop for your organization."
  },

  "workforce-development-intensive": {
    title: "Workforce Development Intensive",
    icon: "Users",
    summary: "A comprehensive workforce readiness training program covering essential professional skills, from communication to career planning. This intensive is ideal for workforce development agencies, community colleges, non-profits, and government programs preparing individuals for workplace success.",
    importance: "The modern workplace demands more than technical skills—employers consistently rank soft skills, professionalism, and workplace readiness as top priorities when hiring. This intensive program bridges the gap between education and employment, ensuring participants are truly job-ready.",
    keyPoints: [
      {
        title: "Workplace Culture and Expectations",
        description: "Understand the unwritten rules of professional environments—attendance, punctuality, dress codes, communication norms, and workplace hierarchy."
      },
      {
        title: "Professional Communication",
        description: "Master essential communication skills including email etiquette, phone professionalism, meeting participation, and how to communicate with supervisors and colleagues."
      },
      {
        title: "Time Management and Organization",
        description: "Learn proven techniques for managing multiple priorities, meeting deadlines, and staying organized in fast-paced work environments."
      },
      {
        title: "Problem-Solving and Critical Thinking",
        description: "Develop the ability to analyze situations, identify solutions, and make sound decisions—skills that employers value across all industries."
      },
      {
        title: "Teamwork and Collaboration",
        description: "Understand your role in team dynamics, learn to collaborate effectively with diverse colleagues, and contribute positively to workplace culture."
      },
      {
        title: "Conflict Resolution",
        description: "Learn strategies for handling workplace disagreements professionally, giving and receiving feedback, and maintaining positive relationships."
      },
      {
        title: "Career Pathway Planning",
        description: "Create a roadmap for career advancement including identifying opportunities, setting goals, and understanding how to grow within an organization."
      }
    ],
    callToAction: "Prepare your participants for workplace success. Request our Workforce Development Intensive for your program."
  },

  "leadership-career-growth": {
    title: "Leadership & Career Growth",
    icon: "Award",
    summary: "Strategies for advancing into leadership roles and maximizing career potential. This workshop focuses on developing leadership capabilities, strategic thinking, and the skills needed to advance into management and executive positions.",
    importance: "Leadership is not just a title—it's a set of skills that can be developed. Whether you're an emerging leader, aspiring manager, or high-potential employee, understanding how to lead effectively and position yourself for advancement is crucial for long-term career success.",
    keyPoints: [
      {
        title: "Leadership Styles and Self-Discovery",
        description: "Explore different leadership styles and identify your natural strengths. Understand how to adapt your approach based on situations and team needs."
      },
      {
        title: "Executive Presence",
        description: "Learn the components of executive presence—confidence, communication, gravitas, and appearance—and how to develop them authentically."
      },
      {
        title: "Strategic Thinking",
        description: "Develop the ability to see the big picture, anticipate challenges, and make decisions that align with organizational goals and long-term success."
      },
      {
        title: "Influencing Without Authority",
        description: "Master the art of persuasion, building coalitions, and driving change even when you don't have formal authority over others."
      },
      {
        title: "Building and Leading Teams",
        description: "Learn how to recruit, develop, motivate, and retain high-performing team members. Understand delegation, accountability, and creating a positive team culture."
      },
      {
        title: "Mentorship and Sponsorship",
        description: "Understand the difference between mentors and sponsors, how to find them, and how to leverage these relationships for career advancement."
      },
      {
        title: "Creating Your Leadership Brand",
        description: "Define what you want to be known for as a leader and create a plan to build that reputation through consistent actions and visibility."
      }
    ],
    callToAction: "Develop the next generation of leaders in your organization. Request our Leadership & Career Growth workshop today."
  },

  "salary-negotiation-mastery": {
    title: "Salary Negotiation Mastery",
    icon: "TrendingUp",
    summary: "Learn proven techniques to negotiate better compensation packages with confidence. This workshop empowers participants with the skills and strategies to negotiate salaries, benefits, and promotions effectively, including role-play exercises and real-world scenarios.",
    importance: "Failing to negotiate your salary can cost you hundreds of thousands of dollars over your career. Yet many professionals don't negotiate because they don't know how or fear negative consequences. This workshop transforms negotiation from a source of anxiety into a strategic skill.",
    keyPoints: [
      {
        title: "Know Your Market Value",
        description: "Learn how to research salary ranges using multiple sources, understand compensation structures, and accurately assess what you're worth in the current market."
      },
      {
        title: "Timing Your Negotiation",
        description: "Understand when to negotiate—and when not to. Learn the optimal moments in the hiring process, annual review cycle, and promotion discussions to have compensation conversations."
      },
      {
        title: "The Total Compensation Package",
        description: "Look beyond base salary to evaluate the full package: bonuses, equity, benefits, retirement contributions, PTO, remote work options, and professional development opportunities."
      },
      {
        title: "Building Your Case",
        description: "Create a compelling argument for your desired compensation using your achievements, market data, and the value you bring to the organization."
      },
      {
        title: "Negotiation Scripts and Tactics",
        description: "Practice specific language and techniques for making your ask, handling objections, and navigating counter-offers with confidence and professionalism."
      },
      {
        title: "Overcoming Fear and Anxiety",
        description: "Address the psychological barriers to negotiation and develop a mindset that views negotiation as a normal, expected part of the professional relationship."
      },
      {
        title: "Negotiating Beyond New Jobs",
        description: "Apply negotiation skills to raises, promotions, project assignments, and other workplace opportunities—not just new job offers."
      }
    ],
    callToAction: "Empower your team to advocate for themselves. Request our Salary Negotiation Mastery workshop today."
  }
};
